import type { IconOption } from '@/components/IconPicker'
import type { AccountType } from '@/api/accounts'

// Curated set offered by the Account Icon picker. Every `name` must be a valid
// lucide-react dynamic icon name (see categoryIcon.tsx) — kept small and relevant to the
// account types this app supports (BANK, CASH, DIGITAL_WALLET, INVESTMENT, RECEIVABLE, PROPERTY, LOAN,
// CREDIT_CARD, PAYABLE) rather than exposing all ~1900 lucide icons. An account created
// before this field existed (or with the picker left blank) has icon: null — getDefaultAccountIcon
// below picks a sensible one for display so a card is never left without a glyph.
export const ACCOUNT_ICON_OPTIONS: IconOption[] = [
  { name: 'landmark', label: 'Bank' },
  { name: 'wallet', label: 'Wallet' },
  { name: 'banknote', label: 'Cash' },
  { name: 'coins', label: 'Coins' },
  { name: 'smartphone', label: 'Digital Wallet' },
  { name: 'piggy-bank', label: 'Savings' },
  { name: 'vault', label: 'Locker / Vault' },
  { name: 'trending-up', label: 'Investments' },
  { name: 'line-chart', label: 'Stocks / Mutual Funds' },
  { name: 'gem', label: 'Gold / Jewelry' },
  { name: 'home', label: 'Home' },
  { name: 'building', label: 'Property' },
  { name: 'building-2', label: 'Business' },
  { name: 'car', label: 'Vehicle' },
  { name: 'graduation-cap', label: 'Education Loan' },
  { name: 'briefcase', label: 'Business Loan' },
  { name: 'credit-card', label: 'Credit Card' },
  { name: 'receipt', label: 'Bills / Payable' },
  { name: 'hand-coins', label: 'Receivable' },
  { name: 'file-text', label: 'Loan Document' },
  { name: 'indian-rupee', label: 'Rupee' },
  { name: 'shield-check', label: 'Insurance' },
  { name: 'key', label: 'Property Keys' },
]

const DEFAULT_ICON_BY_TYPE: Record<AccountType, string> = {
  BANK: 'landmark',
  CASH: 'wallet',
  DIGITAL_WALLET: 'smartphone',
  INVESTMENT: 'trending-up',
  RECEIVABLE: 'hand-coins',
  PROPERTY: 'home',
  LOAN: 'file-text',
  CREDIT_CARD: 'credit-card',
  PAYABLE: 'receipt',
}

// Only BANK and INVESTMENT accounts carry a subType — everything else falls through to
// the type-level default above.
const DEFAULT_ICON_BY_SUB_TYPE: Record<string, string> = {
  SAVINGS: 'piggy-bank',
  SALARY: 'wallet',
  RD: 'piggy-bank',
  FD: 'vault',
  RETIREMENT: 'shield-check',
  MARKET_LINKED: 'line-chart',
}

/** The glyph to show for an account that has no icon set — one per account type, with a
 *  handful of BANK/INVESTMENT sub-types overriding it for a closer match. Never returns
 *  a value outside ACCOUNT_ICON_OPTIONS. */
export function getDefaultAccountIcon(type: AccountType, subType?: string | null): string {
  if (subType && subType in DEFAULT_ICON_BY_SUB_TYPE) {
    return DEFAULT_ICON_BY_SUB_TYPE[subType]
  }
  return DEFAULT_ICON_BY_TYPE[type]
}
